import {
    type ClipboardEvent,
    type FocusEvent,
    type KeyboardEvent,
    type MouseEvent,
    useCallback,
    useRef,
    useState,
} from "react";
import { flushSync } from "react-dom";
import classNames from "classnames";
import { Input as BaseInput } from "@base-ui/react/input";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { Tag } from "@design-system/Tag/Tag";
import { commitCandidates, splitPastedText } from "./tagsInputValues";
import styles from "./TagsInput.module.scss";
import type { TagsInputProps } from "./TagsInput.types";

export type { TagsInputProps, TagsInputSize } from "./TagsInput.types";

const DEFAULT_SEPARATOR_KEYS = ["Enter", " ", ","];

const sizeClasses = {
    lg: styles.lg,
    xl: styles.xl,
};

/**
 * Controlled tag list: `Tag` chips plus a Base UI `Input` for the draft.
 * Library agnostic; bind `value` / `onValueChange` / `onBlur` to any form.
 */
export const TagsInput = ({
    value,
    onValueChange,
    onBlur,
    name,
    disabled = false,
    size = "xl",
    separatorKeys = DEFAULT_SEPARATOR_KEYS,
    normalizeValue,
    isValidValue,
    onInvalidValues,
    className,
    style,
    "data-forced-state": forcedState,
    ref,
    isValueRemovable = () => true,
    ...inputProps
}: TagsInputProps) => {
    const [draft, setDraft] = useState("");
    const inputRef = useRef<HTMLInputElement | null>(null);

    const assignRef = useCallback(
        (node: HTMLElement | null) => {
            const input = node instanceof HTMLInputElement ? node : null;
            mergeRefs([inputRef, ref])(input);
        },
        [ref],
    );

    const commit = (candidates: readonly string[]) => {
        const result = commitCandidates({
            values: value,
            candidates,
            normalizeValue,
            isValidValue,
        });
        if (result.hasChanged) {
            onValueChange(result.values);
        }
        if (result.invalidValues.length > 0) {
            onInvalidValues?.(result.invalidValues);
        }
        return result;
    };

    const commitDraft = () => {
        if (commit([draft]).invalidValues.length === 0) {
            setDraft("");
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (separatorKeys.includes(event.key)) {
            event.preventDefault();
            commitDraft();
        } else if (
            event.key === "Backspace" &&
            draft === "" &&
            value.length > 0
        ) {
            const lastValue = value[value.length - 1];
            if (lastValue !== undefined && isValueRemovable(lastValue)) {
                onValueChange(value.slice(0, -1));
            }
        }
    };

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const { selectionStart, selectionEnd } = event.currentTarget;
        const mergedText =
            draft.slice(0, selectionStart ?? draft.length) +
            event.clipboardData.getData("text") +
            draft.slice(selectionEnd ?? draft.length);
        commit(splitPastedText(mergedText));
        setDraft("");
    };

    const handleRootMouseDown = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target !== inputRef.current) {
            event.preventDefault();
            inputRef.current?.focus();
        }
    };

    const handleRootBlur = (event: FocusEvent<HTMLDivElement>) => {
        const { relatedTarget } = event;
        if (
            relatedTarget instanceof Node &&
            event.currentTarget.contains(relatedTarget)
        ) {
            return;
        }
        // Consumers like Formik validate on blur against the last rendered
        // value, so the committed draft must render before onBlur fires.
        if (draft !== "") {
            flushSync(commitDraft);
        }
        onBlur?.();
    };

    const removeValue = (removedValue: string) => {
        onValueChange(value.filter((item) => item !== removedValue));
        inputRef.current?.focus();
    };

    return (
        <div
            className={classNames(styles.root, sizeClasses[size], className)}
            style={style}
            data-size={size}
            data-disabled={disabled ? "" : undefined}
            data-forced-state={forcedState}
            onMouseDown={handleRootMouseDown}
            onBlur={handleRootBlur}
        >
            {value.map((item) => (
                <Tag
                    key={item}
                    size="lg"
                    onRemove={
                        disabled || !isValueRemovable(item)
                            ? undefined
                            : () => removeValue(item)
                    }
                    removeLabel={`Remove ${item}`}
                >
                    <BreakableText text={item} showTooltip />
                </Tag>
            ))}
            <BaseInput
                {...inputProps}
                ref={assignRef}
                className={styles.control}
                value={draft}
                disabled={disabled}
                onValueChange={setDraft}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
            />
            {name !== undefined && (
                <input
                    type="hidden"
                    name={name}
                    value={value.join(",")}
                    disabled={disabled}
                />
            )}
        </div>
    );
};
